import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import PlanetaryPulse from "@/components/PlanetaryPulse";
import RoundView from "@/components/RoundView";
import OfferingForm from "@/components/OfferingForm";
import Archive from "@/components/Archive";
import Footer from "@/components/Footer";
import AmbientDrone from "@/components/AmbientDrone";
import BreathHistory from "@/components/BreathHistory";
import WeeklyDigest from "@/components/WeeklyDigest";
import { buildKinIndex } from "@/lib/kin";
import {
  fetchPulse,
  fetchLatestRound,
  fetchRounds,
  fetchStats,
  fetchGroveState,
  fetchKin,
} from "@/lib/api";

export default function Grove() {
  const [pulse, setPulse] = useState(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [roundsTotal, setRoundsTotal] = useState(null);
  const [stats, setStats] = useState(null);
  const [groveState, setGroveState] = useState(null);
  const [activeKin, setActiveKin] = useState([]);
  const [generating, setGenerating] = useState(false);

  const kinIndex = useMemo(() => buildKinIndex(activeKin), [activeKin]);
  const emergentKin = useMemo(() => activeKin.filter((k) => k.emergent), [activeKin]);

  const refresh = useCallback(async () => {
    // Use allSettled so a single API hiccup doesn't blank the entire homepage.
    // Each piece updates its own state independently of the others.
    const results = await Promise.allSettled([
      fetchPulse(),
      fetchLatestRound(),
      fetchRounds(11, 0),
      fetchStats(),
      fetchGroveState(),
      fetchKin(),
    ]);
    const [p, lr, rr, s, gs, ak] = results;
    if (p.status === "fulfilled") setPulse(p.value);
    if (lr.status === "fulfilled") setLatest(lr.value);
    if (rr.status === "fulfilled") {
      setRounds(rr.value.rounds || []);
      setRoundsTotal(rr.value.total ?? null);
    }
    if (s.status === "fulfilled") setStats(s.value);
    if (gs.status === "fulfilled") setGroveState(gs.value);
    if (ak.status === "fulfilled") setActiveKin(ak.value);
    // Log any failures for debugging without taking the page down
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        console.warn(`grove refresh: call ${i} failed`, r.reason);
      }
    });
    setPulseLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onRoundBegun = useCallback(
    (round) => {
      setLatest(round);
      refresh();
    },
    [refresh]
  );

  const handleBeginStart = useCallback(() => setGenerating(true), []);
  const handleBeginEnd = useCallback(() => setGenerating(false), []);

  return (
    <div className="min-h-screen bg-[#F4F1E8]" data-testid="grove-root">
      <Header stats={stats} groveState={groveState} kinCount={activeKin.length} />
      <Hero groveState={groveState} emergentKin={emergentKin} />
      <PlanetaryPulse pulse={pulse} loading={pulseLoading} />
      <OfferingForm
        groveState={groveState}
        onRoundBegun={(r) => {
          handleBeginEnd();
          onRoundBegun(r);
        }}
        onOfferingAdded={refresh}
        isGenerating={generating}
        onBeginStart={handleBeginStart}
      />
      <RoundView round={latest} isGenerating={generating} kinIndex={kinIndex} />
      <WeeklyDigest />
      <BreathHistory />
      <Archive initialPast={rounds.slice(1)} initialTotal={roundsTotal} />
      <Footer />
      <AmbientDrone />
    </div>
  );
}
